import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ClickAwayListener, Popper } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  approvalCreateApprovalLineAtom,
  approvalCreateDeactivatedNamesAtom,
  approvalCreateReferrersAtom,
} from './atoms';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import ToolTip from '@/components/@IntranetLibrary/ToolTip';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import DeactivatedBadge from '@/components/Approval/Common/DeactivatedBadge';
import OnLeaveBadge from '@/components/Approval/Common/OnLeaveBadge';
import FixedMemberLock from '@/components/Approval/Common/FixedMemberLock';
import { useUserSearch } from '@/components/Approval/@components/ReferrerField/hooks/useUserSearch';
import type { Referrer } from '@/components/Approval/@components/ReferrerField/types';
import useFixedApprovalMembers from '@/hooks/approval/useFixedApprovalMembers';
import {
  useFormFixedApprovers,
  useFormFixedCoApprovers,
  useFormFixedReferrers,
} from '@/hooks/approval/useFormFixedMembers';
import type { ApprovalTypeReferrer } from '@/types/approval';
import useDeactivatedApprovers from '@/hooks/approval/useDeactivatedApprovers';
import useDeactivatedUsers from '@/hooks/approval/useDeactivatedUsers';
import useMemberOrganizations from '@/hooks/approval/useMemberOrganizations';
import useOnLeaveUsers from '@/hooks/approval/useOnLeaveUsers';
import useRecentApprovers from '@/hooks/approval/useRecentApprovers';
import useLayoutSize from '@/hooks/useLayoutSize';
import useScrollLock from '@/hooks/useScrollLock';
import {
  pickMissingFixedMembers,
  pinFixedApproversToEnd,
} from '@/utils/approval/fixedApprovalMembers';
import flowToApprovalLineInput from '@/utils/approval/flowToApprovalLineInput';
import makeFlowId from '@/utils/approval/makeFlowId';
import {
  CO_APPROVER_RESTRICTIONS,
  findCoApproverPickViolation,
} from '@/utils/approval/submitChecks';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import {
  type ClientRect,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragOverEvent,
  type DragStartEvent,
  type DraggableAttributes,
  MeasuringStrategy,
  type Modifier,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ApprovalIcon from '@/public/icons/approval/approval-line.svg';
import PlusIcon from '@/public/icons/approval/plus.svg';
import ProfilesIcon from '@/public/icons/approval/profiles.svg';
import DotsVerticalIcon from '@/public/icons/approval/dots-vertical.svg';
import CloseIcon from '@/public/icons/approval/close.svg';
import CloseCircleIcon from '@/public/icons/approval/close-circle.svg';
import SearchEmptyIcon from '@/public/icons/approval/search-empty.svg';
import AddNextLineIcon from '@/public/icons/approval/add-next-line.svg';
import DragHandleIcon from '@/public/icons/approval/drag-handle.svg';
import SwapIcon from '@/public/icons/approval/swap-horizontal.svg';
import BoxedTextButton from '@/components/@IntranetLibrary/Button/BoxedTextButton';
import SearchIcon from '@/public/icons/search2.svg';
import EmptyIcon from '@/public/icons/empty.svg';
import RecentApprovalLineList, {
  withDeactivatedFlags,
  type RecentApprovalLineSummary,
} from './RecentApprovalLineList';
import RecentApprovalLineDetail from './RecentApprovalLineDetail';
import useTabOutClose from '@/components/Approval/@hooks/useTabOutClose';
import ResetTextButton from '@/components/Approval/@components/ResetTextButton';
import highlightKeyword from '@/components/Approval/@components/highlightKeyword';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import {
  ApprovalLineActionRow,
  HistoryEntryButton,
  HistoryPopoverClose,
  HistoryPopoverHeader,
  HistoryPopoverPaper,
  BottomSearchPositioner,
  CooperationAfterSlot,
  CooperationBlock,
  CooperationHeader,
  CooperationHeaderIcon,
  CooperationHeaderLeft,
  CooperationHeaderTitle,
  CooperationInnerBottom,
  CooperationTopRail,
  EmptyHistoryIconWrap,
  EmptyHistoryText,
  EmptyHistoryWithIcon,
  FlowAvatar,
  FlowBottomSpace,
  FlowCover,
  FlowCloseButton,
  FlowInlineAddButton,
  FlowInlineAddSlot,
  FlowInlineSearch,
  FlowKebabButton,
  FlowSwapButton,
  DragTypeChangeBadge,
  DragOverlayFrame,
  FlowList,
  FlowMemberDept,
  FlowMemberInfo,
  FlowMemberName,
  FlowMemberNameRow,
  FlowMemberPosition,
  FlowNumberHead,
  FlowRail,
  FlowRailSegment,
  FlowRailTop,
  FlowRow,
  FlowSearchClearButton,
  FlowSearchDropdown,
  FlowSearchDropdownHeader,
  FlowSearchOption,
  FlowSearchOptionAvatar,
  FlowSearchOptionDept,
  FlowSearchOptionInfo,
  FlowSearchOptionName,
  FlowUserCard,
  OrderToggleLabel,
  RecentSearchBar,
  RecentSearchButton,
  RecentSearchInput,
  RecentSearchSection,
  SearchBar,
  SearchIconWrapper,
  SearchInput,
  SearchSectionContent,
  SectionBox,
  SectionDivider,
  SectionTitle,
  SectionTitleLabel,
  SectionTitleRight,
  KEBAB_SHEET_CLOSE_ICON,
  KEBAB_SHEET_CLOSE_STROKE,
  KEBAB_SHEET_ICON_BOX,
  KebabSheetContainer,
  KebabSheetItem,
  KebabSheetItemIcon,
} from './styles';

type ApprovalType = '결재' | '협조';

export type FlowKind = 'approver' | 'cooperation-search' | 'search';

export type FlowMemberData = {
  id: string;
  /** 원본 사용자(passport) id — 검색 시 이미 추가된 사람 제외용. id는 DnD row 식별자라 별도 보관 */
  userId?: string;
  kind: FlowKind;
  name?: string;
  positionName?: string;
  divisionName?: string;
  departmentName?: string;
  profileImageUrl?: string;
};

export type FlowEntry =
  | { type: 'member'; data: FlowMemberData }
  | {
      type: 'cooperation';
      id: string;
      members: FlowMemberData[];
    };

const getEntryId = (entry: FlowEntry) =>
  entry.type === 'member' ? entry.data.id : entry.id;

type CooperationEntry = Extract<FlowEntry, { type: 'cooperation' }>;

const isCooperation = (entry: FlowEntry): entry is CooperationEntry =>
  entry.type === 'cooperation';

/** 멤버가 속한 컨테이너 식별자 — 협조 블록은 블록 id, 일반 결재자는 이 상수 */
const ROOT_CONTAINER_ID = 'root';

/** 작성 모달(1300)·스낵바(1400) 위, 툴팁(1500) 아래 */
const DRAG_OVERLAY_Z_INDEX = 1450;

/** 멤버 id 가 아니면(= 협조 블록 id) null — 블록 드래그와 멤버 드래그를 가르는 판정 */
const findMemberContainerId = (entries: FlowEntry[], memberId: string) => {
  for (const entry of entries) {
    if (entry.type === 'member') {
      if (entry.data.id === memberId) return ROOT_CONTAINER_ID;
    } else if (entry.members.some((m) => m.id === memberId)) {
      return entry.id;
    }
  }
  return null;
};

const findMemberData = (entries: FlowEntry[], memberId: string) => {
  for (const entry of entries) {
    if (entry.type === 'member') {
      if (entry.data.id === memberId) return entry.data;
    } else {
      const found = entry.members.find((m) => m.id === memberId);
      if (found) return found;
    }
  }
  return null;
};

const isYInsideRect = (y: number | null, rect?: ClientRect) =>
  y !== null && !!rect && y >= rect.top && y <= rect.bottom;

const isYBelowRectMiddle = (y: number | null, rect: ClientRect) =>
  y !== null && y > rect.top + rect.height / 2;

/**
 * over 대상이 가리키는 컨테이너. 협조 블록 id 가 over 로 잡히는 경우가 두 가지라
 * (블록이 비어서 블록 자체가 유일한 후보 / 블록을 지나쳐 일반 목록으로 재정렬)
 * 드래그 사각형 중심이 블록 안인지로 갈라낸다 — collisionDetection 과 같은 기준.
 */
const resolveDropContainerId = (
  entries: FlowEntry[],
  overId: string,
  overRect: ClientRect,
  activeCenterY: number | null,
) => {
  const memberContainerId = findMemberContainerId(entries, overId);
  if (memberContainerId) return memberContainerId;
  const isCoopBlock = entries.some((e) => isCooperation(e) && e.id === overId);
  if (!isCoopBlock) return null;
  return isYInsideRect(activeCenterY, overRect) ? overId : ROOT_CONTAINER_ID;
};

/**
 * 멤버를 다른 컨테이너(일반 목록 ↔ 협조 블록)로 옮긴다.
 * 멤버가 빠져 비게 된 협조 블록은 여기서 지우지 않는다 — 드래그 도중 되돌아올
 * drop 대상이 남아 있어야 해서, 삭제는 드롭 확정 시 pruneEmptyCooperations 가 맡는다.
 */
const moveMemberBetweenContainers = (
  entries: FlowEntry[],
  memberId: string,
  toContainerId: string,
  overId: string,
  insertAfterOver: boolean,
): FlowEntry[] => {
  const member = findMemberData(entries, memberId);
  if (!member) return entries;

  const without = entries.flatMap<FlowEntry>((entry) => {
    if (entry.type === 'member') {
      return entry.data.id === memberId ? [] : [entry];
    }
    if (!entry.members.some((m) => m.id === memberId)) return [entry];
    return [
      { ...entry, members: entry.members.filter((m) => m.id !== memberId) },
    ];
  });

  if (toContainerId === ROOT_CONTAINER_ID) {
    const overIdx = without.findIndex((e) => getEntryId(e) === overId);
    const at =
      overIdx < 0 ? without.length : overIdx + (insertAfterOver ? 1 : 0);
    const next = [...without];
    next.splice(at, 0, { type: 'member', data: member });
    return next;
  }

  return without.map((entry) => {
    if (!isCooperation(entry) || entry.id !== toContainerId) return entry;
    const overIdx = entry.members.findIndex((m) => m.id === overId);
    const at =
      overIdx < 0 ? entry.members.length : overIdx + (insertAfterOver ? 1 : 0);
    const members = [...entry.members];
    members.splice(at, 0, member);
    return { ...entry, members };
  });
};

type FlowLockTarget = 'approver' | 'coApprover';

const pruneEmptyCooperations = (entries: FlowEntry[]) =>
  entries.filter((e) => e.type === 'member' || e.members.length > 0);

/** 협조 → 일반 결재. 협조 묶음 바로 다음 자리로 나온다(묶음이 비면 그 자리) */
const moveCoopMemberToRoot = (
  entries: FlowEntry[],
  memberId: string,
): FlowEntry[] => {
  const member = findMemberData(entries, memberId);
  const from = findMemberContainerId(entries, memberId);
  if (!member || !from || from === ROOT_CONTAINER_ID) return entries;
  return entries.flatMap<FlowEntry>((e) => {
    if (!isCooperation(e) || e.id !== from) return [e];
    const members = e.members.filter((m) => m.id !== memberId);
    const moved: FlowEntry = { type: 'member', data: member };
    return members.length > 0 ? [{ ...e, members }, moved] : [moved];
  });
};

/** 일반 → 협조. 협조 묶음이 있으면 맨 끝에, 없으면 그 자리에 새 묶음 */
const moveRootMemberToCoop = (
  entries: FlowEntry[],
  memberId: string,
): FlowEntry[] => {
  const member = findMemberData(entries, memberId);
  if (!member || findMemberContainerId(entries, memberId) !== ROOT_CONTAINER_ID)
    return entries;
  if (entries.some(isCooperation)) {
    return entries
      .filter((e) => getEntryId(e) !== memberId)
      .map((e) =>
        isCooperation(e) ? { ...e, members: [...e.members, member] } : e,
      );
  }
  return entries.map<FlowEntry>((e) =>
    getEntryId(e) === memberId
      ? { type: 'cooperation', id: makeFlowId('coop'), members: [member] }
      : e,
  );
};

/** @dnd-kit/modifiers 미설치 — restrictToVerticalAxis 동일 동작 인라인 구현 */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

type SearchUser = Referrer;

const MIN_KEYWORD_LENGTH = 2;
const RECENT_USERS_STORAGE_KEY = 'approval-line-recent-users';
const RECENT_USERS_MAX = 4;

const loadRecentUsers = (): SearchUser[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_USERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as SearchUser[]).slice(0, RECENT_USERS_MAX);
  } catch {
    return [];
  }
};

const saveRecentUsers = (users: SearchUser[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      RECENT_USERS_STORAGE_KEY,
      JSON.stringify(users.slice(0, RECENT_USERS_MAX)),
    );
  } catch {
    // 무시
  }
};

const RECENT_USERS_CHANGE_EVENT = 'approval-line-recent-users-change';

/** 최근 선택 사용자 목록 localStorage 공유 — 다중 인스턴스 동기화용 CustomEvent broadcast */
const useRecentUsers = () => {
  const [recent, setRecent] = useState<SearchUser[]>(() => loadRecentUsers());

  useEffect(() => {
    const sync = () => setRecent(loadRecentUsers());
    window.addEventListener('storage', sync);
    window.addEventListener(RECENT_USERS_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(RECENT_USERS_CHANGE_EVENT, sync);
    };
  }, []);

  // 이 목록은 localStorage 저장본이라 재직자 검색 필터를 안 거침 —
  // 저장 후 퇴사한 사람은 서버 판정으로 노출에서만 거른다.
  // 저장본까지 지우면 오판(일시 404) 시 되돌릴 수 없어 지우지 않는다.
  const recentIds = useMemo(() => recent.map((u) => u.id), [recent]);
  const { deactivatedIds } = useDeactivatedUsers(recentIds);
  const visibleRecent = useMemo(
    () => recent.filter((u) => !deactivatedIds.has(u.id)),
    [recent, deactivatedIds],
  );

  const addRecent = useCallback((user: SearchUser) => {
    const current = loadRecentUsers();
    const next = [user, ...current.filter((u) => u.id !== user.id)].slice(
      0,
      RECENT_USERS_MAX,
    );
    saveRecentUsers(next);
    setRecent(next);
    window.dispatchEvent(new Event(RECENT_USERS_CHANGE_EVENT));
  }, []);

  return { recent: visibleRecent, addRecent };
};

/** 결재라인에 이미 포함된 사용자 ID 집합 — 검색/최근 선택의 중복 추가 방지용 */
const ExcludedUsersContext = createContext<Set<string>>(new Set());

/**
 * 결재라인 행에 배지로 표시할 사람별 상태.
 * - deactivatedIds: 퇴사자 passportId 집합. ApprovalLineSection 이 두 조회 결과를 합쳐서
 *   채운다 — 문서의 결재라인 퇴사자 조회(편집 모드) + 현재 라인 멤버 전체의 재직 여부 조회.
 * - onLeaveIds: 휴직자 passportId 집합. 퇴사와 달리 상신을 막지 않아 표시에만 쓴다.
 */
type MemberStatusContextValue = {
  deactivatedIds: Set<string>;
  onLeaveIds: Set<string>;
};

const EMPTY_MEMBER_STATUS: MemberStatusContextValue = {
  deactivatedIds: new Set(),
  onLeaveIds: new Set(),
};

const MemberStatusContext =
  createContext<MemberStatusContextValue>(EMPTY_MEMBER_STATUS);

/** 퇴사 판정 단일 지점 — 배지 표시와 상신 차단이 공유 */
const isMemberDeactivated = (
  data: FlowMemberData,
  deactivatedIds: Set<string>,
) => Boolean(data.userId && deactivatedIds.has(data.userId));

/**
 * 휴직 판정 단일 지점.
 * 퇴사자로도 잡히면 퇴사 배지만 남긴다 — 결재가 진행되지 않는다는 점은 같은데 퇴사 쪽이
 * 상신 차단까지 걸리는 더 강한 상태라, 배지를 겹쳐 달면 무엇을 고쳐야 하는지 오히려 흐려진다.
 */
const isMemberOnLeave = (
  data: FlowMemberData,
  { onLeaveIds, deactivatedIds }: MemberStatusContextValue,
) =>
  Boolean(
    data.userId &&
      onLeaveIds.has(data.userId) &&
      !deactivatedIds.has(data.userId),
  );

/** ArrowDown 진입 공통 — 닫혀 있으면 열면서, 열려 있으면 즉시 첫 행으로 포커스 이동 */
const arrowDownIntoList = (
  e: React.KeyboardEvent,
  isOpen: boolean,
  openList: () => void,
  rows: (HTMLButtonElement | null)[],
) => {
  if (e.key !== 'ArrowDown') return;
  // 한글 조합 중 포커스를 옮기면 조합 글자가 중복 확정됨
  if (e.nativeEvent.isComposing) return;
  e.preventDefault();
  if (!isOpen) {
    openList();
    // 행 렌더/ref 채움은 커밋 후 — 같은 키 입력 한 번으로 첫 행까지 진입.
    // rAF 는 hidden 탭에서 보류되므로 setTimeout 사용
    setTimeout(() => rows[0]?.focus(), 0);
    return;
  }
  rows[0]?.focus();
};

/** 리스트 내부 방향키 roving — 첫 행에서 ArrowUp 이면 onExitTop(트리거로 복귀) */
const moveRovingFocus = (
  e: React.KeyboardEvent,
  rows: (HTMLButtonElement | null)[],
  onExitTop: () => void,
) => {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const idx = rows.findIndex((el) => el === e.target);
  if (idx < 0) return;
  e.preventDefault();
  if (e.key === 'ArrowDown') rows[idx + 1]?.focus();
  else if (idx === 0) onExitTop();
  else rows[idx - 1]?.focus();
};

/** 검색 패널 노출 리스트 — 키워드가 짧을 땐 최근 선택, 그 이상이면 검색 결과 */
const useSearchPanelData = (keyword: string, excludedIds: Set<string>) => {
  const tooShort = keyword.trim().length < MIN_KEYWORD_LENGTH;
  const { data, isLoading } = useUserSearch(keyword);
  const { recent } = useRecentUsers();

  const showRecent = tooShort;
  const rawList = showRecent ? recent : isLoading ? [] : data;
  const list = useMemo(
    () => rawList.filter((u) => !excludedIds.has(u.id)),
    [rawList, excludedIds],
  );
  return { list, showRecent, isLoading };
};

type SearchResultPanelProps = {
  keyword: string;
  list: SearchUser[];
  showRecent: boolean;
  isLoading: boolean;
  /** 행 button ref 배열 — 부모가 인풋 ArrowDown 에서 첫 행 focus 진입에 사용 */
  rowsRef: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  /** 첫 행에서 ArrowUp — 인풋으로 포커스 복귀 */
  onFocusInput: () => void;
  onSelect: (user: SearchUser) => void;
};

/** 키보드 이동은 Tab/방향키 모두 실제 포커스 이동(roving) — 하이라이트 방식은 선택 위치가 눈에 띄지 않음 */
const SearchResultPanel = ({
  keyword,
  list,
  showRecent,
  isLoading,
  rowsRef,
  onFocusInput,
  onSelect,
}: SearchResultPanelProps) => {
  const { t } = useTranslation();
  const headerHasResults = showRecent ? list.length > 0 : !isLoading;

  // 리스트가 줄면 잔여 ref 가 포커스 대상으로 남지 않게 자름
  useEffect(() => {
    rowsRef.current.length = list.length;
  }, [rowsRef, list]);

  return (
    <FlowSearchDropdown
      onKeyDown={(e) => moveRovingFocus(e, rowsRef.current, onFocusInput)}
    >
      <FlowSearchDropdownHeader
        muted={list.length === 0}
        sx={
          !showRecent && isLoading
            ? {
                display: 'flex',
                justifyContent: 'center',
                padding: '12px 8px',
              }
            : undefined
        }
      >
        {showRecent ? (
          list.length > 0 ? (
            t('최근 선택')
          ) : (
            t('검색어를 {{min}}자 이상 입력해 주세요', {
              min: MIN_KEYWORD_LENGTH,
            })
          )
        ) : isLoading ? (
          <Spinner theme="black" size="16px" />
        ) : list.length === 0 ? (
          t('검색 결과가 없어요.')
        ) : (
          <>
            {t('검색결과')}
            <span className="count">{t('{{n}}건', { n: list.length })}</span>
          </>
        )}
      </FlowSearchDropdownHeader>
      {headerHasResults &&
        list.map((u, i) => (
          <FlowSearchOption
            key={u.id}
            type="button"
            ref={(el) => {
              rowsRef.current[i] = el;
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(u)}
          >
            <FlowSearchOptionAvatar>
              <img
                src={u.profileImageUrl || '/images/dummy/default-profile.svg'}
                alt={u.name}
                onError={(e) => {
                  const fallback = '/images/dummy/default-profile.svg';
                  if (!e.currentTarget.src.includes(fallback)) {
                    e.currentTarget.src = fallback;
                  }
                }}
              />
            </FlowSearchOptionAvatar>
            <FlowSearchOptionInfo>
              <FlowSearchOptionName>
                {showRecent ? u.name : highlightKeyword(u.name, keyword)}
              </FlowSearchOptionName>
              {(u.divisionName || u.departmentName) && (
                <FlowSearchOptionDept>
                  {[
                    u.divisionName,
                    resolveDepartmentName(
                      { passportId: u.id, departmentName: u.departmentName },
                      u.departmentName,
                    ),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </FlowSearchOptionDept>
              )}
            </FlowSearchOptionInfo>
          </FlowSearchOption>
        ))}
    </FlowSearchDropdown>
  );
};

type FlowInlineSearchFieldProps = {
  autoFocus?: boolean;
  onBlur?: () => void;
  onSelect?: (user: SearchUser) => void;
};

/** 결재라인 슬롯 인라인 검색 */
const FlowInlineSearchField = ({
  autoFocus = false,
  onBlur,
  onSelect,
}: FlowInlineSearchFieldProps) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const [panelOpen, setPanelOpen] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rowsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const excludedIds = useContext(ExcludedUsersContext);
  const { list, showRecent, isLoading } = useSearchPanelData(
    keyword,
    excludedIds,
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    arrowDownIntoList(e, panelOpen, () => setPanelOpen(true), rowsRef.current);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!onBlur) return;
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    onBlur();
  };

  // Tab 으로 결과행에 포커스가 가 있을 때도 Escape 로 닫고 인풋 복귀
  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !panelOpen) return;
    if (e.nativeEvent.isComposing) return;
    // 하단 검색바와 동일하게 focus → close 순서 — 인풋에 onFocus(재오픈)가 생겨도 닫힘이 이기게
    inputRef.current?.focus();
    setPanelOpen(false);
  };

  const handleClear = () => {
    setKeyword('');
    inputRef.current?.focus();
  };

  return (
    <FlowInlineSearch onBlur={handleBlur} onKeyDown={handleContainerKeyDown}>
      <input
        ref={inputRef}
        type="text"
        placeholder={t('소속 또는 이름 검색')}
        autoFocus={autoFocus}
        value={keyword}
        onChange={(e) => {
          setKeyword(e.target.value);
          setPanelOpen(true);
        }}
        onClick={() => setPanelOpen(true)}
        onKeyDown={handleInputKeyDown}
      />
      {keyword ? (
        <FlowSearchClearButton
          type="button"
          aria-label={t('검색어 지우기')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
        >
          <CloseCircleIcon width={18} height={18} />
        </FlowSearchClearButton>
      ) : (
        <SearchIcon width={20} height={20} />
      )}
      {onSelect && panelOpen && (
        <SearchResultPanel
          keyword={keyword}
          list={list}
          showRecent={showRecent}
          isLoading={isLoading}
          rowsRef={rowsRef}
          onFocusInput={() => inputRef.current?.focus()}
          onSelect={onSelect}
        />
      )}
    </FlowInlineSearch>
  );
};

/** 행 전체에 드래그가 걸려 있어 버튼을 길게 누르면 드래그로 바뀌고 클릭이 막힌다 */
const stopDragStart = {
  onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
};

type FlowItemViewProps = {
  data: FlowMemberData;
  step: number;
  variant?: 'normal' | 'cooperation';
  /** 순번 해제 협조 멤버 — 숫자 박스 없이 레일 라인만 표시 */
  hideNumberHead?: boolean;
  hideTopRail?: boolean;
  hideBottomRail?: boolean;
  hideBottomSpace?: boolean;
  canInsert?: boolean;
  onInsert?: () => void;
  autoFocus?: boolean;
  onSearchBlur?: () => void;
  onSearchSelect?: (user: SearchUser) => void;
  onRemove?: () => void;
  onKebabOpen?: () => void;
  /** 일반 결재 ↔ 협조결재 바꾸기 — 협조자가 1명이면 드래그로는 묶음째 움직여서 이 버튼으로만 바꾼다 */
  onSwapType?: () => void;
  swapLabel?: string;
  /** 어드민이 양식에 고정한 결재자/협조결재자 — 삭제 버튼 대신 자물쇠 표시 (PORTAL-13909) */
  locked?: FlowLockTarget | null;
  rowRef?: (el: HTMLElement | null) => void;
  rowStyle?: React.CSSProperties;
  rowListeners?: Record<string, unknown>;
  dragHandleRef?: (el: HTMLElement | null) => void;
  dragHandleProps?: DraggableAttributes;
  /** 협조자 1명 행 — 묶음 드래그가 행에 걸려 있어 번호 칸에 드래그 아이콘을 보인다 */
  groupDraggable?: boolean;
  /** 커서를 따라다니는 사본 */
  isDragging?: boolean;
  /** 목록에 남는 원래 자리 */
  isDragSource?: boolean;
};

const FlowItemView = ({
  data,
  step,
  variant = 'normal',
  hideNumberHead = false,
  hideTopRail = false,
  hideBottomRail = false,
  hideBottomSpace = false,
  canInsert = false,
  onInsert,
  autoFocus = false,
  onSearchBlur,
  onSearchSelect,
  onRemove,
  onKebabOpen,
  onSwapType,
  swapLabel,
  locked = null,
  rowRef,
  rowStyle,
  rowListeners,
  dragHandleRef,
  dragHandleProps,
  groupDraggable = false,
  isDragging = false,
  isDragSource = false,
}: FlowItemViewProps) => {
  const { t } = useTranslation();
  const isSearch = data.kind === 'cooperation-search' || data.kind === 'search';
  // 맨 끝에 고정된 결재자처럼 드래그를 막은 행은 핸들 없이 순번만 보인다
  const isDragHandle = !!dragHandleRef || groupDraggable;
  const showDragHandle = isSearch || isDragHandle;
  // 묶음 드래그 행은 번호 칸이 아니라 행 전체로 끄므로 버튼 역할은 주지 않는다
  const isHandleButton = isSearch || !!dragHandleRef;
  const memberStatus = useContext(MemberStatusContext);
  const isDeactivated = isMemberDeactivated(data, memberStatus.deactivatedIds);
  const isOnLeave = isMemberOnLeave(data, memberStatus);
  const department = resolveDepartmentName(
    { passportId: data.userId, departmentName: data.departmentName },
    data.departmentName,
  );

  return (
    <FlowRow
      ref={rowRef}
      className="flow-row"
      style={rowStyle}
      isDragging={isDragging}
      isDragSource={isDragSource}
      variant={variant}
      {...(rowListeners ?? {})}
    >
      <FlowRail>
        <FlowRailTop
          className="rail-seg"
          variant={variant}
          hidden={hideTopRail}
        />
        {!hideNumberHead && (
          <FlowNumberHead
            ref={dragHandleRef}
            className={isDragHandle ? 'number-head' : undefined}
            variant={variant}
            active={isSearch}
            {...(dragHandleProps ?? {})}
            role={isHandleButton ? 'button' : undefined}
            aria-label={
              isHandleButton
                ? isSearch
                  ? t('결재자 선택')
                  : t('드래그해서 순서 변경')
                : undefined
            }
          >
            {showDragHandle ? (
              <>
                <span className="number-text">{step}</span>
                <span className="drag-handle" aria-hidden>
                  <DragHandleIcon width={9} height={12} />
                </span>
              </>
            ) : (
              <span>{step}</span>
            )}
          </FlowNumberHead>
        )}
        <FlowRailSegment
          className="rail-seg"
          variant={variant}
          hidden={hideBottomRail}
        />
      </FlowRail>
      <FlowCover className="flow-cover">
        {isSearch ? (
          <FlowUserCard>
            <FlowInlineSearchField
              autoFocus={autoFocus}
              onBlur={onSearchBlur}
              onSelect={onSearchSelect}
            />
          </FlowUserCard>
        ) : (
          <FlowUserCard className="approve-user-card">
            <FlowAvatar>
              <img
                src={
                  data.profileImageUrl || '/images/dummy/default-profile.svg'
                }
                alt={data.name ?? ''}
                onError={(e) => {
                  const fallback = '/images/dummy/default-profile.svg';
                  if (!e.currentTarget.src.includes(fallback)) {
                    e.currentTarget.src = fallback;
                  }
                }}
              />
            </FlowAvatar>
            <FlowMemberInfo>
              <FlowMemberNameRow>
                <span className="name-text">
                  {isDeactivated && <DeactivatedBadge />}
                  {isOnLeave && <OnLeaveBadge />}
                  <FlowMemberName>{data.name}</FlowMemberName>
                  {data.positionName && (
                    <FlowMemberPosition>{data.positionName}</FlowMemberPosition>
                  )}
                </span>
              </FlowMemberNameRow>
              {department && (
                <FlowMemberDept>
                  <span className="department">{department}</span>
                </FlowMemberDept>
              )}
            </FlowMemberInfo>
            <FlowKebabButton
              type="button"
              aria-label={t('더보기')}
              {...stopDragStart}
              onClick={(e) => {
                e.stopPropagation();
                onKebabOpen?.();
              }}
            >
              <DotsVerticalIcon width={16} height={16} />
            </FlowKebabButton>
            {!locked && onSwapType && swapLabel && (
              <ToolTip title={swapLabel} placement="top">
                <FlowSwapButton
                  type="button"
                  className="row-swap"
                  aria-label={swapLabel}
                  {...stopDragStart}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwapType();
                  }}
                >
                  <SwapIcon width={16} height={16} />
                </FlowSwapButton>
              </ToolTip>
            )}
            {locked ? (
              <FixedMemberLock target={locked} />
            ) : (
              <FlowCloseButton
                type="button"
                className="row-close"
                aria-label={t('결재자 제외')}
                {...stopDragStart}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.();
                }}
              >
                <CloseIcon width={13} height={13} />
              </FlowCloseButton>
            )}
          </FlowUserCard>
        )}
        {canInsert ? (
          <FlowInlineAddSlot className="insert-hover-slot" variant={variant}>
            <FlowInlineAddButton
              type="button"
              aria-label={t('결재자 추가')}
              onClick={onInsert}
              variant={variant}
            >
              <PlusIcon width={12} height={12} />
            </FlowInlineAddButton>
          </FlowInlineAddSlot>
        ) : (
          <FlowBottomSpace hidden={hideBottomSpace} />
        )}
      </FlowCover>
    </FlowRow>
  );
};

type SortableMemberProps = Omit<
  FlowItemViewProps,
  'rowRef' | 'rowStyle' | 'dragHandleRef' | 'dragHandleProps'
> & {
  id: string;
  draggable?: boolean;
  /** 협조자 1명 — 카드를 잡으면 협조 묶음이 통째로 움직이게 묶음의 드래그를 건다 */
  groupListeners?: Record<string, unknown>;
};

const SortableMember = ({
  id,
  draggable = true,
  groupListeners,
  ...rest
}: SortableMemberProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <FlowItemView
      {...rest}
      rowRef={setNodeRef}
      rowStyle={style}
      rowListeners={groupListeners ?? (draggable ? listeners : undefined)}
      groupDraggable={!!groupListeners}
      dragHandleRef={draggable ? setActivatorNodeRef : undefined}
      dragHandleProps={draggable ? attributes : undefined}
      isDragSource={isDragging}
    />
  );
};

type SortableCooperationProps = {
  entry: Extract<FlowEntry, { type: 'cooperation' }>;
  startStep: number;
  orderedCooperation: boolean;
  onToggleOrdered: (v: boolean) => void;
  onRemoveMember: (memberId: string) => void;
  onInsertAfterMember: (afterMemberId: string) => void;
  onSelectMember: (memberId: string, user: SearchUser) => void;
  onKebabOpenForMember?: (memberId: string) => void;
  onSwapMember?: (memberId: string) => void;
  /** 어드민이 양식에 고정한 멤버인지 — 삭제 버튼 대신 자물쇠 표시 */
  getLockTarget?: (member: FlowMemberData) => FlowLockTarget | null;
  /** 결재라인 첫 번째 위치면 박스 위로 이어지는 상단 라인 숨김 */
  hideTopRail?: boolean;
  /** 밖에서 끌고 온 멤버가 이 블록에 들어와 있는 동안 외곽선 강조 */
  isDropTarget?: boolean;
  /** 협조결재 블록 다음(top-level 위치)에 결재라인을 추가할 수 있는지 여부 */
  canInsertAfter?: boolean;
  onInsertAfter?: () => void;
};

const SortableCooperation = ({
  entry,
  startStep,
  orderedCooperation,
  onToggleOrdered,
  onRemoveMember,
  onInsertAfterMember,
  onSelectMember,
  onKebabOpenForMember,
  onSwapMember,
  getLockTarget,
  hideTopRail = false,
  isDropTarget = false,
  canInsertAfter,
  onInsertAfter,
}: SortableCooperationProps) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
  };

  // 순번을 끄면 번호 박스가 없어 세로선이 카드 중간에서 잘려 보인다
  const railEndsAtLastCard = !canInsertAfter && !orderedCooperation;
  const isSingleMember =
    entry.members.length === 1 && entry.members[0].kind !== 'search';

  return (
    <>
      <CooperationBlock
        ref={setNodeRef}
        className="coop-block"
        data-drop-target={isDropTarget || undefined}
        style={style}
        isDragging={isDragging}
      >
        <CooperationTopRail
          className={hideTopRail ? undefined : 'coop-top-rail'}
          hidden={hideTopRail}
        />
        <CooperationHeader className="coop-header">
          <CooperationHeaderLeft>
            <CooperationHeaderIcon
              ref={setActivatorNodeRef as any}
              className="coop-handle"
              aria-label={t('드래그해서 순서 변경')}
              isDragging={isDragging}
              {...attributes}
              {...listeners}
            >
              <span className="coop-icon-layer coop-profiles" aria-hidden>
                {orderedCooperation ? (
                  <ProfilesIcon width={20} height={20} />
                ) : (
                  <span className="coop-step-num">{startStep}</span>
                )}
              </span>
              <span className="coop-icon-layer coop-drag-handle" aria-hidden>
                <DragHandleIcon width={9} height={12} />
              </span>
            </CooperationHeaderIcon>
            <CooperationHeaderTitle variant="body2">
              {t('협조결재')}
            </CooperationHeaderTitle>
          </CooperationHeaderLeft>
          <OrderToggleLabel
            control={
              <Checkbox
                size="small"
                checked={orderedCooperation}
                onChange={(e) => onToggleOrdered(e.target.checked)}
              />
            }
            label={t('순서대로 결재')}
          />
        </CooperationHeader>
        <SortableContext
          items={entry.members.map((m) => m.id)}
          strategy={verticalListSortingStrategy}
        >
          {entry.members.map((m, mIdx) => {
            const isSearchMember = m.kind === 'search';
            const isLastMember = mIdx === entry.members.length - 1;
            return (
              <SortableMember
                key={m.id}
                id={m.id}
                draggable={!isSearchMember && !isSingleMember}
                groupListeners={isSingleMember ? listeners : undefined}
                data={m}
                step={orderedCooperation ? startStep + mIdx : startStep}
                variant="cooperation"
                hideNumberHead={!orderedCooperation}
                hideBottomRail={
                  isLastMember && !canInsertAfter && !railEndsAtLastCard
                }
                hideBottomSpace={isLastMember}
                canInsert={!isLastMember && !isSearchMember}
                onInsert={() => onInsertAfterMember(m.id)}
                autoFocus={isSearchMember}
                onSearchBlur={
                  isSearchMember ? () => onRemoveMember(m.id) : undefined
                }
                onSearchSelect={
                  isSearchMember
                    ? (user) => onSelectMember(m.id, user)
                    : undefined
                }
                onRemove={
                  isSearchMember ? undefined : () => onRemoveMember(m.id)
                }
                locked={isSearchMember ? null : getLockTarget?.(m) ?? null}
                onKebabOpen={
                  isSearchMember
                    ? undefined
                    : () => onKebabOpenForMember?.(m.id)
                }
                onSwapType={
                  isSearchMember ? undefined : () => onSwapMember?.(m.id)
                }
                swapLabel={t('일반 결재로 변경')}
              />
            );
          })}
        </SortableContext>
        <CooperationInnerBottom
          className={canInsertAfter ? 'coop-inner-bottom' : undefined}
          showRail={canInsertAfter}
          aria-hidden
        />
      </CooperationBlock>
      {/* 박스 안에 넣으면 영역이 넓어져 아래에 놓으려던 결재자가 안으로 들어간다.
          밖에 있으면 드래그 이동을 못 받으므로 style 은 따로 같이 걸어준다 */}
      {canInsertAfter && (
        <CooperationAfterSlot style={style}>
          <FlowInlineAddSlot>
            <FlowInlineAddButton
              type="button"
              aria-label={t('결재자 추가')}
              onClick={onInsertAfter}
            >
              <PlusIcon width={12} height={12} />
            </FlowInlineAddButton>
          </FlowInlineAddSlot>
        </CooperationAfterSlot>
      )}
    </>
  );
};

type HistoryTab = 'recent' | 'my';

type ApprovalLineSectionProps = {
  /** 저장된 문서의 documentId. 신규 작성이면 undefined */
  documentId?: string;
  /**
   * 퇴사자 조회 대상 — 편집으로 연 문서만. 이 화면에서 처음 저장한 문서는 백엔드가 바로 못 찾아 422(Sentry 누적)
   */
  deactivatedCheckDocumentId?: string;
  /**
   * 양식 코드. 고정 협조결재자·고정 참조인 판별에 쓰므로 두 모드 모두 필요하다.
   * 자동 push 만 신규 작성 모드 한정.
   */
  apvType?: number;
  /** 편집 모드 1회 hydrate 입력. 신규 작성은 미전달해 빈 결재라인으로 시작한다 */
  initialFlow?: FlowEntry[];
  initialOrderedCooperation?: boolean;
};

const ApprovalLineSection = ({
  documentId,
  deactivatedCheckDocumentId,
  apvType,
  initialFlow,
  initialOrderedCooperation,
}: ApprovalLineSectionProps = {}) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const showComingSoon = useCallback(() => {
    dialog.show({
      message: t('준비 중인 기능이에요\n조금만 기다려 주세요.'),
    });
  }, [dialog, t]);
  const [openHistory, setOpenHistory] = useState(false);
  const { deactivatedIds: docDeactivatedIds } = useDeactivatedApprovers(
    deactivatedCheckDocumentId,
  );
  const { isMobile } = useLayoutSize();
  // 목록이 팝오버 한 겹으로 들어가면서 탭은 없앴다. '나의 결재라인'이 생기면
  // 이 팝오버 안에 탭을 다시 얹는 자리가 된다 — 지금 화면은 탭이 없어도 어색하지 않다.
  const activeTab: HistoryTab = 'recent';
  /** 팝오버 기준점 — 카드 제목줄의 '최근 결재라인' 버튼 */
  const [historyAnchorEl, setHistoryAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  /** 입력 중 keyword. 검색 버튼 또는 Enter로 확정될 때까지 API 호출에 반영되지 않음. */
  const [recentInputKeyword, setRecentInputKeyword] = useState('');
  /** 마지막으로 검색 버튼이 확정한 keyword. SWR key의 일부. */
  const [recentSubmittedKeyword, setRecentSubmittedKeyword] = useState('');
  /** 1-based 현재 페이지. 검색 재실행 시 1로 리셋. */
  const [recentPage, setRecentPage] = useState(1);
  const recentSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [recentDetail, setRecentDetail] =
    useState<RecentApprovalLineSummary | null>(null);
  const [type, setType] = useState<ApprovalType>('결재');
  const [orderedCooperation, setOrderedCooperation] = useState(false);
  const { addRecent } = useRecentUsers();
  // hydrate 는 mount 후 effect 에서 — SSR hydration mismatch 회피 위해 initializer 는 빈 배열.
  const [flow, setFlow] = useState<FlowEntry[]>(() => []);
  /** 드래그 중인 멤버 id — 외곽선 강조/오버레이 전용. 소속은 항상 최신 flow 에서 다시 찾는다 */
  const [draggingMemberId, setDraggingMemberId] = useState<string | null>(null);
  /** 드래그를 시작한 곳이 협조 묶음인지 — 놓는 곳과 다르면 결재 유형이 바뀐다는 표시를 띄운다 */
  const [dragFromCoop, setDragFromCoop] = useState(false);
  const initialHydratedRef = useRef(false);
  useEffect(() => {
    if (initialHydratedRef.current) return;
    initialHydratedRef.current = true;
    if (initialFlow && initialFlow.length > 0) {
      setFlow(initialFlow);
      if (typeof initialOrderedCooperation === 'boolean') {
        setOrderedCooperation(initialOrderedCooperation);
      }
    }
  }, [initialFlow, initialOrderedCooperation]);

  /**
   * 어드민이 양식에 고정한 협조결재자.
   * 자동 push 는 신규 작성에서만 — 편집 모드는 서버 저장값이 우선이라 없던 사람을 끼워넣지 않는다.
   * 다만 고정 여부 판별(삭제 잠금)은 양식 메타 기준이라 두 모드 모두 동작한다.
   */
  const isNewMode = !documentId;
  const formFixedApprovers = useFormFixedApprovers(apvType);
  const formFixedCoApprovers = useFormFixedCoApprovers(apvType);
  /** 참조인 고정 판별 — 최근 결재라인 적용이 참조인 atom 을 덮어쓸 때 고정분을 살리는 데 쓴다 */
  const fixedReferrers = useFormFixedReferrers(apvType);
  const fixedReferrerIds = useMemo(
    () => new Set(fixedReferrers.map((r) => r.id)),
    [fixedReferrers],
  );

  // 지금 결재라인에 있는 멤버 전원의 재직 여부를 서버에 물어본다.
  // 문서 단위 퇴사자 조회는 편집 모드에서만 응답이 있으므로, 둘을 합친 셋을
  // 배지 표시와 상신 차단이 쓴다 — 어떤 경로로 추가된 멤버든 빠짐없이 걸리게.
  const flowMemberIds = useMemo(() => {
    const ids: string[] = [];
    flow.forEach((entry) => {
      if (entry.type === 'member') {
        if (entry.data.userId) ids.push(entry.data.userId);
      } else {
        entry.members.forEach((m) => {
          if (m.userId) ids.push(m.userId);
        });
      }
    });
    // 고정 후보도 함께 조회한다 — 결재라인에 넣기 전에 퇴사 여부를 알아야 자동 추가를 막는다
    formFixedApprovers.forEach((r) => ids.push(r.id));
    formFixedCoApprovers.forEach((r) => ids.push(r.id));
    return ids;
  }, [flow, formFixedApprovers, formFixedCoApprovers]);
  const {
    deactivatedIds: liveDeactivatedIds,
    isResolved: deactivationResolved,
  } = useDeactivatedUsers(flowMemberIds);
  const deactivatedIds = useMemo(() => {
    if (liveDeactivatedIds.size === 0) return docDeactivatedIds;
    return new Set([...docDeactivatedIds, ...liveDeactivatedIds]);
  }, [docDeactivatedIds, liveDeactivatedIds]);
  // 휴직 여부는 퇴사 판정과 같은 passport 응답에서 나오고 응답 캐시도 공유하므로
  // 추가 호출이 없다. 문서 단위 조회에는 휴직 정보가 없어 live 판정만 쓴다.
  const { onLeaveIds } = useOnLeaveUsers(flowMemberIds);
  const memberStatus = useMemo(
    () => ({ deactivatedIds, onLeaveIds }),
    [deactivatedIds, onLeaveIds],
  );

  /**
   * 협조결재자 지정 부서 1명 제한 양식 — 검색 선택·결재자 칸에서 끌어 옮기기·바꾸기 버튼으로 넣은 사람만
   * 소속이 확인되는 대로 검사해 어긋나면 빼고 안내한다.
   * 최근 결재라인·편집 문서처럼 통째로 들어온 결재라인은 지우지 않고 상신 검사에 맡긴다.
   */
  const coApproverRestriction =
    apvType !== undefined ? CO_APPROVER_RESTRICTIONS[apvType] : undefined;
  const [pickedCoApproverIds, setPickedCoApproverIds] = useState<string[]>([]);
  const markCoApproverPicked = (memberId: string) => {
    if (!coApproverRestriction) return;
    setPickedCoApproverIds((prev) =>
      prev.includes(memberId) ? prev : [...prev, memberId],
    );
  };
  const flowOrganizations = useMemberOrganizations(flowMemberIds);
  useEffect(() => {
    if (!coApproverRestriction || pickedCoApproverIds.length === 0) return;
    if (!flowOrganizations) return;

    const coApproversOf = (entries: FlowEntry[]) =>
      entries
        .filter(isCooperation)
        .flatMap((e) => e.members)
        .filter((m) => m.userId);

    let next = flow;
    let message: string | null = null;
    pickedCoApproverIds.forEach((memberId, index) => {
      const coApprovers = coApproversOf(next);
      const member = coApprovers.find((m) => m.id === memberId);
      if (!member?.userId) return;
      // 나중에 고른 사람은 아직 없던 것으로 본다 — 먼저 고른 사람이 대신 빠지지 않게
      const laterPicked = new Set(pickedCoApproverIds.slice(index + 1));
      const othersCount = coApprovers.filter(
        (m) => m.id !== memberId && !laterPicked.has(m.id),
      ).length;
      const violation = findCoApproverPickViolation(
        coApproverRestriction,
        flowOrganizations.get(member.userId) ?? null,
        othersCount,
      );
      if (!violation) return;
      message ??= violation;
      next = pruneEmptyCooperations(
        next.map((e) =>
          isCooperation(e)
            ? { ...e, members: e.members.filter((m) => m.id !== memberId) }
            : e,
        ),
      );
    });

    setPickedCoApproverIds([]);
    if (next !== flow) setFlow(next);
    if (message) snackbar.showError({ message: t(message) });
  }, [
    coApproverRestriction,
    pickedCoApproverIds,
    flowOrganizations,
    flow,
    snackbar,
    t,
  ]);

  /**
   * 퇴사자는 고정 대상에서 뺀다 — 자동 추가도, 삭제 잠금도 하지 않는다.
   * 퇴사자가 섞이면 CreateFooter 가 상신을 차단하는데, 잠겨 있거나 지워도 보정이 다시 넣으면
   * 빠져나갈 방법이 없어진다. 양식 메타는 BE 가 퇴사자를 걸러 내려주지만 그 기준과 여기
   * live 판정이 어긋날 수 있어 화면에서 막아둔다.
   */
  const fixedApprovers = useMemo(
    () => formFixedApprovers.filter((r) => !deactivatedIds.has(r.id)),
    [formFixedApprovers, deactivatedIds],
  );
  const fixedCoApprovers = useMemo(
    () => formFixedCoApprovers.filter((r) => !deactivatedIds.has(r.id)),
    [formFixedCoApprovers, deactivatedIds],
  );

  /**
   * 고정 협조결재자가 결재라인에 없으면 협조 블록에 채워 넣는다. 이미 있으면 no-op(멱등).
   * deps 에 flow 가 있어야 최근 결재라인 적용/초기화처럼 통째로 덮어쓴 뒤에도 다시 채워진다.
   */
  const ensureFixedCoApprovers = useCallback(
    (members: ApprovalTypeReferrer[]) => {
      const collectUserIds = (entries: FlowEntry[]) => {
        const ids = new Set<string>();
        entries.forEach((entry) => {
          if (entry.type === 'member') {
            if (entry.data.userId) ids.add(entry.data.userId);
          } else {
            entry.members.forEach((m) => {
              if (m.userId) ids.add(m.userId);
            });
          }
        });
        return ids;
      };

      if (pickMissingFixedMembers(members, collectUserIds(flow)).length === 0) {
        return;
      }

      setFlow((prev) => {
        const newMembers: FlowMemberData[] = pickMissingFixedMembers(
          members,
          collectUserIds(prev),
        ).map((r) => ({
          id: `coop-fixed-${r.id}`,
          userId: r.id,
          kind: 'approver',
          name: r.name,
          positionName: r.positionName,
          divisionName: r.divisionName,
          departmentName: r.departmentName,
        }));
        if (newMembers.length === 0) return prev;
        const hasCoop = prev.some((e) => e.type === 'cooperation');
        if (hasCoop) {
          return prev.map((e) =>
            e.type === 'cooperation'
              ? { ...e, members: [...e.members, ...newMembers] }
              : e,
          );
        }
        return [
          ...prev,
          {
            type: 'cooperation',
            id: makeFlowId('coop-fixed'),
            members: newMembers,
          },
        ];
      });
    },
    [flow],
  );

  // 판정이 도착하기 전에 보정을 돌리면 퇴사자가 잠깐 자동 추가된다(판정 후 풀리긴 하지만
  // 첫 진입에서 깜빡인다). 판정 도착까지 보정을 미룬다 — error 면 도착으로 쳐서 진행.
  const { isFixed: isFixedCoApprover } = useFixedApprovalMembers(
    fixedCoApprovers,
    isNewMode && deactivationResolved,
    ensureFixedCoApprovers,
  );

  /**
   * 고정 결재자는 편집 모드에서도 맨 끝 자리를 지킨다(드래그·다른 결재자 추가 뒤 복구).
   * 없는 사람을 채워 넣는 건 신규 작성에서만 — 편집은 서버 저장값이 우선.
   */
  const ensureFixedApprovers = useCallback(
    (members: ApprovalTypeReferrer[]) => {
      if (pinFixedApproversToEnd(flow, members, isNewMode) === flow) return;
      setFlow((prev) => pinFixedApproversToEnd(prev, members, isNewMode));
    },
    [flow, isNewMode],
  );
  // 드래그 중엔 flow 가 미리보기 상태라 보정하면 행이 튄다 — 드롭 확정 뒤 한 번에 맞춘다
  const { isFixed: isFixedApprover } = useFixedApprovalMembers(
    fixedApprovers,
    deactivationResolved && !draggingMemberId,
    ensureFixedApprovers,
  );

  const setApprovalLineAtom = useSetAtom(approvalCreateApprovalLineAtom);
  const setDeactivatedNamesAtom = useSetAtom(
    approvalCreateDeactivatedNamesAtom,
  );
  useEffect(() => {
    setApprovalLineAtom(flowToApprovalLineInput(flow, orderedCooperation));
    setDeactivatedNamesAtom(
      flow
        .flatMap((e) => (e.type === 'member' ? [e.data] : e.members))
        .filter((m) => isMemberDeactivated(m, deactivatedIds))
        .map((m) => m.name ?? ''),
    );
  }, [
    flow,
    orderedCooperation,
    deactivatedIds,
    setApprovalLineAtom,
    setDeactivatedNamesAtom,
  ]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  const collisionDetection: CollisionDetection = (args) => {
    const activeId = args.active.id as string;

    // 협조결재 블록을 드래그 중이면, 자기 자식 멤버들은 drop 후보에서 제외
    const activeCoopBlock = flow.find(
      (e): e is CooperationEntry => isCooperation(e) && e.id === activeId,
    );
    if (activeCoopBlock) {
      const excluded = new Set(activeCoopBlock.members.map((m) => m.id));
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (c) => !excluded.has(c.id as string),
        ),
      });
    }

    // 멤버 드래그 — 드래그 사각형 중심 y 가 들어가 있는 협조 블록이 곧 대상 컨테이너.
    // 블록과 그 멤버를 한 후보군에 섞으면 블록 중심이 이겨서 순번 지정이 뭉개지므로,
    // 블록 안이면 그 블록 멤버만 / 밖이면 일반 목록 항목만 남긴다.
    const centerY = args.collisionRect.top + args.collisionRect.height / 2;
    const hoveredCoop = flow.find(
      (e): e is CooperationEntry =>
        isCooperation(e) &&
        isYInsideRect(centerY, args.droppableRects.get(e.id)),
    );
    const allowed = new Set<string>(
      hoveredCoop
        ? // 마지막 멤버까지 끌어낸 빈 블록은 자기 자신이 유일한 복귀 지점
          hoveredCoop.members.length > 0
          ? hoveredCoop.members.map((m) => m.id)
          : [hoveredCoop.id]
        : flow.map(getEntryId),
    );
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((c) =>
        allowed.has(c.id as string),
      ),
    });
  };

  /** 컨테이너 이동은 드래그 중 flow 를 실제로 고쳐서 미리보기를 만들므로 취소 복원용 스냅샷 */
  const flowBeforeDragRef = useRef<FlowEntry[] | null>(null);

  const getActiveCenterY = (event: DragOverEvent | DragEndEvent) => {
    const rect = event.active.rect.current.translated;
    return rect ? rect.top + rect.height / 2 : null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const memberId = event.active.id as string;
    // 컨테이너를 못 찾으면 멤버가 아니라 협조 블록 자체 드래그 — 멤버 추적 대상이 아니다
    if (!findMemberContainerId(flow, memberId)) return;
    flowBeforeDragRef.current = flow;
    typeChangeRef.current = null;
    setDraggingMemberId(memberId);
    setDragFromCoop(
      findMemberContainerId(flow, memberId) !== ROOT_CONTAINER_ID,
    );
  };

  /** 옮기기 직전 소속 — 바뀐 flow 가 반영된 뒤 결재 유형이 실제로 바뀌었는지 보고 알림을 띄운다 */
  const typeChangeRef = useRef<{
    memberId: string;
    wasCoop: boolean;
    before: FlowEntry[];
  } | null>(null);
  useEffect(() => {
    const change = typeChangeRef.current;
    if (!change) return;
    // 협조결재자 제한 검사가 끝난 뒤에 판단 — 제한에 걸려 빠지면 오류 알림만 남긴다
    if (pickedCoApproverIds.includes(change.memberId)) return;
    typeChangeRef.current = null;
    const container = findMemberContainerId(flow, change.memberId);
    const member = findMemberData(flow, change.memberId);
    if (!container || !member) return;
    const toCoop = container !== ROOT_CONTAINER_ID;
    if (toCoop === change.wasCoop) return;
    const after = flow;
    snackbar.showSuccess({
      message: toCoop
        ? t('{{name}}님을 협조결재로 변경했습니다.', { name: member.name })
        : t('{{name}}님을 일반 결재로 변경했습니다.', { name: member.name }),
      duration: 4000,
      action: {
        text: t('되돌리기'),
        // 그사이 결재라인을 고쳤으면 그 내용은 두고 이 사람의 결재 유형만 되돌린다
        onClick: () =>
          setFlow((prev) => {
            if (prev === after) return change.before;
            return toCoop
              ? moveCoopMemberToRoot(prev, change.memberId)
              : moveRootMemberToCoop(prev, change.memberId);
          }),
      },
    });
  }, [flow, pickedCoApproverIds, snackbar, t]);

  const endMemberDrag = () => {
    flowBeforeDragRef.current = null;
    setDraggingMemberId(null);
  };

  // 경계를 넘는 순간 소속을 옮겨 협조 블록이 자리를 벌리고 순번이 다시 매겨지게 한다
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = active.id as string;
    const overId = over.id as string;
    const centerY = getActiveCenterY(event);

    setFlow((prev) => {
      const from = findMemberContainerId(prev, activeId);
      if (!from) return prev;
      const to = resolveDropContainerId(prev, overId, over.rect, centerY);
      if (!to || to === from) return prev;
      return moveMemberBetweenContainers(
        prev,
        activeId,
        to,
        overId,
        isYBelowRectMiddle(centerY, over.rect),
      );
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeId = active.id as string;
    const flowBeforeDrag = flowBeforeDragRef.current;
    const fromBeforeDrag = flowBeforeDrag
      ? findMemberContainerId(flowBeforeDrag, activeId)
      : null;
    if (fromBeforeDrag === ROOT_CONTAINER_ID) {
      markCoApproverPicked(activeId);
    }
    endMemberDrag();
    const centerY = getActiveCenterY(event);

    const settleDrop = (prev: FlowEntry[]): FlowEntry[] => {
      const from = findMemberContainerId(prev, activeId);
      // 멤버 드래그면 dragOver 가 이미 소속을 옮겨놨을 수 있어 빈 블록 정리가 항상 필요
      const settle = (next: FlowEntry[]) =>
        from ? pruneEmptyCooperations(next) : next;
      if (!over || activeId === over.id) return settle(prev);
      const overId = over.id as string;

      if (from) {
        const to = resolveDropContainerId(prev, overId, over.rect, centerY);
        if (!to) return settle(prev);
        if (to !== from) {
          return settle(
            moveMemberBetweenContainers(
              prev,
              activeId,
              to,
              overId,
              isYBelowRectMiddle(centerY, over.rect),
            ),
          );
        }
        if (to === ROOT_CONTAINER_ID) {
          const oldIdx = prev.findIndex((e) => getEntryId(e) === activeId);
          const newIdx = prev.findIndex((e) => getEntryId(e) === overId);
          if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx)
            return settle(prev);
          return settle(arrayMove(prev, oldIdx, newIdx));
        }
        return settle(
          prev.map((e) => {
            if (!isCooperation(e) || e.id !== to) return e;
            const oldIdx = e.members.findIndex((m) => m.id === activeId);
            const newIdx = e.members.findIndex((m) => m.id === overId);
            if (oldIdx < 0 || newIdx < 0) return e;
            return { ...e, members: arrayMove(e.members, oldIdx, newIdx) };
          }),
        );
      }

      // 협조결재 블록 자체 드래그 = 블록 순서 이동. over 가 남의 협조 멤버면 그 블록 위치로 매핑
      const overCoop = prev.find(
        (e): e is CooperationEntry =>
          isCooperation(e) && e.members.some((m) => m.id === overId),
      );
      const oldIndex = prev.findIndex((e) => getEntryId(e) === activeId);
      const newIndex = prev.findIndex(
        (e) => getEntryId(e) === (overCoop ? overCoop.id : overId),
      );
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    };

    if (flowBeforeDrag && fromBeforeDrag) {
      typeChangeRef.current = {
        memberId: activeId,
        wasCoop: fromBeforeDrag !== ROOT_CONTAINER_ID,
        before: flowBeforeDrag,
      };
    }
    setFlow(settleDrop);
  };

  const handleDragCancel = () => {
    const snapshot = flowBeforeDragRef.current;
    endMemberDrag();
    if (snapshot) setFlow(snapshot);
  };

  const handleInsertAfter = (afterId: string) => {
    setFlow((prev) => {
      const afterIdx = prev.findIndex((e) => getEntryId(e) === afterId);
      if (afterIdx < 0) return prev;
      const newEntry: FlowEntry = {
        type: 'member',
        data: {
          id: makeFlowId('search'),
          kind: 'search',
        },
      };
      return [
        ...prev.slice(0, afterIdx + 1),
        newEntry,
        ...prev.slice(afterIdx + 1),
      ];
    });
  };

  /**
   * 어드민 고정 결재자/협조결재자는 어느 경로로도 빠지지 않는다 (X 버튼/케밥/초기화).
   * 퇴사자는 fixedApprovers/fixedCoApprovers 단계에서 이미 빠져 있어 잠기지 않는다.
   */
  const getLockTarget = (member: FlowMemberData): FlowLockTarget | null => {
    if (isFixedApprover(member.userId)) return 'approver';
    if (isFixedCoApprover(member.userId)) return 'coApprover';
    return null;
  };
  const isLockedMember = (member: FlowMemberData) =>
    getLockTarget(member) !== null;

  const isLockedMemberId = (memberId: string) => {
    const member = findMemberData(flow, memberId);
    return member ? isLockedMember(member) : false;
  };

  /** 지울 게 하나도 없으면(전원 잠김) 초기화 버튼을 감춘다 */
  const hasRemovableFlowMember = flow.some((entry) =>
    entry.type === 'member'
      ? !isLockedMember(entry.data)
      : entry.members.some((m) => !isLockedMember(m)),
  );

  /** 잠긴 멤버만 남기고 나머지를 걷어낸 flow — 초기화 버튼 전용 */
  const keepLockedMembers = (entries: FlowEntry[]): FlowEntry[] =>
    entries.flatMap<FlowEntry>((entry) => {
      if (entry.type === 'member') {
        // 협조 블록 밖으로 옮겨진 고정 결재자도 남긴다
        return isLockedMember(entry.data) ? [entry] : [];
      }
      const members = entry.members.filter(isLockedMember);
      return members.length > 0 ? [{ ...entry, members }] : [];
    });

  /**
   * 결재라인 초기화 — 잠긴 협조결재자만 남기고 비운다.
   * 남긴 게 없으면 빈 결재라인이 되어 기존과 동일하게 동작한다.
   */
  const handleResetFlow = () => {
    setFlow(keepLockedMembers);
  };

  const handleRemoveEntry = (id: string) => {
    if (isLockedMemberId(id)) return;
    setFlow((prev) => prev.filter((e) => getEntryId(e) !== id));
  };

  const handleRemoveCooperationMember = (coopId: string, memberId: string) => {
    if (isLockedMemberId(memberId)) return;
    setFlow((prev) =>
      prev.flatMap((e) => {
        if (e.type === 'cooperation' && e.id === coopId) {
          const nextMembers = e.members.filter((m) => m.id !== memberId);
          if (nextMembers.length === 0) return [];
          return [{ ...e, members: nextMembers }];
        }
        return [e];
      }),
    );
  };

  const handleSwapType = (memberId: string, toCoop: boolean) => {
    const member = findMemberData(flow, memberId);
    if (!member || isLockedMember(member)) return;
    const next = toCoop
      ? moveRootMemberToCoop(flow, memberId)
      : moveCoopMemberToRoot(flow, memberId);
    if (next === flow) return;
    if (toCoop) markCoApproverPicked(memberId);
    typeChangeRef.current = { memberId, wasCoop: !toCoop, before: flow };
    setFlow(next);
  };

  const userToMemberData = (user: SearchUser): FlowMemberData => ({
    id: makeFlowId('approver'),
    userId: user.id,
    kind: 'approver',
    name: user.name,
    positionName: user.positionName,
    divisionName: user.divisionName,
    departmentName: user.departmentName,
    profileImageUrl: user.profileImageUrl,
  });

  const handleSelectFromInlineSearch = (entryId: string, user: SearchUser) => {
    setFlow((prev) =>
      prev.map((e) => {
        if (e.type === 'member' && e.data.id === entryId) {
          return { type: 'member', data: userToMemberData(user) };
        }
        return e;
      }),
    );
    addRecent(user);
  };

  const handleInsertInCooperation = (coopId: string, afterMemberId: string) => {
    setFlow((prev) =>
      prev.map((e) => {
        if (e.type === 'cooperation' && e.id === coopId) {
          const afterIdx = e.members.findIndex((m) => m.id === afterMemberId);
          if (afterIdx < 0) return e;
          const newMember: FlowMemberData = {
            id: makeFlowId('search'),
            kind: 'search',
          };
          return {
            ...e,
            members: [
              ...e.members.slice(0, afterIdx + 1),
              newMember,
              ...e.members.slice(afterIdx + 1),
            ],
          };
        }
        return e;
      }),
    );
  };

  const handleSelectInCooperation = (
    coopId: string,
    memberId: string,
    user: SearchUser,
  ) => {
    const picked = userToMemberData(user);
    setFlow((prev) =>
      prev.map((e) => {
        if (e.type === 'cooperation' && e.id === coopId) {
          return {
            ...e,
            members: e.members.map((m) => (m.id === memberId ? picked : m)),
          };
        }
        return e;
      }),
    );
    markCoApproverPicked(picked.id);
    addRecent(user);
  };

  const handleAppendFromBottomSearch = (user: SearchUser) => {
    addRecent(user);
    const newMember = userToMemberData(user);

    if (type === '협조') {
      markCoApproverPicked(newMember.id);
      setFlow((prev) => {
        const coopIdx = prev.findIndex((e) => e.type === 'cooperation');
        if (coopIdx >= 0) {
          return prev.map((e) =>
            e.type === 'cooperation'
              ? { ...e, members: [...e.members, newMember] }
              : e,
          );
        }
        return [
          ...prev,
          {
            type: 'cooperation',
            id: makeFlowId('coop'),
            members: [newMember],
          },
        ];
      });
    } else {
      setFlow((prev) => [...prev, { type: 'member', data: newMember }]);
    }

    // 키보드(Enter) 선택 시 행이 언마운트되며 포커스 유실 — 인풋 복귀.
    // focus() 가 onFocus(재오픈)를 동기 발화하므로 닫기는 그 뒤에
    bottomSearchInputRef.current?.focus();
    setBottomKeyword('');
    setBottomSearchOpen(false);
  };

  const [bottomKeyword, setBottomKeyword] = useState('');
  const [bottomSearchOpen, setBottomSearchOpen] = useState(false);
  const bottomSearchInputRef = useRef<HTMLInputElement | null>(null);

  const closeBottomSearch = useCallback(() => {
    setBottomSearchOpen(false);
  }, []);
  // Tab 이탈 시에만 닫음 — 외부 클릭(relatedTarget=null)은 ClickAwayListener 담당.
  const handleBottomSearchBlur = useTabOutClose<HTMLDivElement>(
    bottomSearchOpen,
    closeBottomSearch,
  );

  type KebabTarget =
    | { kind: 'topLevel'; entryId: string }
    | { kind: 'coop'; coopId: string; memberId: string };
  const [kebabTarget, setKebabTarget] = useState<KebabTarget | null>(null);
  const closeKebab = () => setKebabTarget(null);
  useScrollLock(Boolean(kebabTarget));

  const handleKebabInsertNext = () => {
    if (!kebabTarget) return;
    const target = kebabTarget;
    closeKebab();
    requestAnimationFrame(() => {
      if (target.kind === 'topLevel') {
        handleInsertAfter(target.entryId);
      } else {
        handleInsertInCooperation(target.coopId, target.memberId);
      }
    });
  };
  /** 케밥 대상이 고정 협조결재자면 '결재라인에서 삭제' 항목 자체를 감춘다 */
  const kebabTargetLocked = kebabTarget
    ? isLockedMemberId(
        kebabTarget.kind === 'topLevel'
          ? kebabTarget.entryId
          : kebabTarget.memberId,
      )
    : false;

  const handleKebabSwap = () => {
    if (!kebabTarget) return;
    const target = kebabTarget;
    closeKebab();
    requestAnimationFrame(() => {
      if (target.kind === 'topLevel') {
        handleSwapType(target.entryId, true);
      } else {
        handleSwapType(target.memberId, false);
      }
    });
  };

  const handleKebabRemove = () => {
    if (!kebabTarget) return;
    const target = kebabTarget;
    closeKebab();
    requestAnimationFrame(() => {
      if (target.kind === 'topLevel') {
        handleRemoveEntry(target.entryId);
      } else {
        handleRemoveCooperationMember(target.coopId, target.memberId);
      }
    });
  };

  const entryIds = flow.map(getEntryId);

  // 드래그 중인 멤버가 현재 속한 블록을 강조 — 출발지 무관(협조 멤버를 잡고 블록 안에 머물러도 강조).
  // handleDragOver 가 소속을 실시간으로 옮기므로, 블록 밖으로 나가면 ROOT 가 되어 자동 해제된다.
  const draggedMemberContainerId = draggingMemberId
    ? findMemberContainerId(flow, draggingMemberId)
    : null;
  const dropTargetCoopId =
    draggedMemberContainerId && draggedMemberContainerId !== ROOT_CONTAINER_ID
      ? draggedMemberContainerId
      : null;

  const draggedMember = draggingMemberId
    ? findMemberData(flow, draggingMemberId)
    : null;
  const draggedVariant =
    draggedMemberContainerId && draggedMemberContainerId !== ROOT_CONTAINER_ID
      ? 'cooperation'
      : 'normal';

  let step = 0;
  const rendered: React.ReactNode[] = [];
  const lastIndex = flow.length - 1;
  const draggedMemberId = draggingMemberId;
  let draggedStep = 0;

  flow.forEach((entry, idx) => {
    const isFirst = idx === 0;
    const isLast = idx === lastIndex;

    if (entry.type === 'member') {
      step += 1;
      if (entry.data.id === draggedMemberId) draggedStep = step;
      const isSearchEntry = entry.data.kind === 'search';
      rendered.push(
        <SortableMember
          key={entry.data.id}
          id={entry.data.id}
          draggable={!isSearchEntry && !isFixedApprover(entry.data.userId)}
          data={entry.data}
          step={step}
          hideTopRail={isFirst}
          hideBottomRail={isLast}
          hideBottomSpace={isLast}
          canInsert={!isLast && !isSearchEntry}
          onInsert={() => handleInsertAfter(entry.data.id)}
          autoFocus={isSearchEntry}
          onSearchBlur={
            isSearchEntry ? () => handleRemoveEntry(entry.data.id) : undefined
          }
          onSearchSelect={
            isSearchEntry
              ? (user) => handleSelectFromInlineSearch(entry.data.id, user)
              : undefined
          }
          onRemove={
            isSearchEntry ? undefined : () => handleRemoveEntry(entry.data.id)
          }
          locked={getLockTarget(entry.data)}
          onKebabOpen={
            isSearchEntry
              ? undefined
              : () =>
                  setKebabTarget({ kind: 'topLevel', entryId: entry.data.id })
          }
          onSwapType={
            isSearchEntry
              ? undefined
              : () => handleSwapType(entry.data.id, true)
          }
          swapLabel={t('협조결재로 변경')}
        />,
      );
    } else {
      const startStep = step + 1;
      // 순서대로 처리하면 협조자가 한 단계씩 쓰고 다음 결재자가 그만큼 밀린다.
      step += orderedCooperation ? entry.members.length : 1;
      const draggedIdx = entry.members.findIndex(
        (m) => m.id === draggedMemberId,
      );
      if (draggedIdx >= 0) {
        draggedStep = orderedCooperation ? startStep + draggedIdx : startStep;
      }
      rendered.push(
        <SortableCooperation
          key={entry.id}
          entry={entry}
          startStep={startStep}
          orderedCooperation={orderedCooperation}
          onToggleOrdered={setOrderedCooperation}
          onRemoveMember={(memberId) =>
            handleRemoveCooperationMember(entry.id, memberId)
          }
          onInsertAfterMember={(afterMemberId) =>
            handleInsertInCooperation(entry.id, afterMemberId)
          }
          onSelectMember={(memberId, user) =>
            handleSelectInCooperation(entry.id, memberId, user)
          }
          getLockTarget={getLockTarget}
          hideTopRail={isFirst}
          isDropTarget={entry.id === dropTargetCoopId}
          canInsertAfter={!isLast}
          onInsertAfter={() => handleInsertAfter(entry.id)}
          onKebabOpenForMember={(memberId) =>
            setKebabTarget({ kind: 'coop', coopId: entry.id, memberId })
          }
          onSwapMember={(memberId) => handleSwapType(memberId, false)}
        />,
      );
    }
  });

  // 최근 결재라인 조회 — 패널 표시 중이면 자동 fetch. 빈 keyword도 유효(최근 N개).
  const {
    items: recentItems,
    meta: recentMeta,
    isLoading: recentLoading,
    isValidating: recentValidating,
  } = useRecentApprovers({
    enabled: openHistory && activeTab === 'recent',
    keyword: recentSubmittedKeyword,
    type: apvType,
    listSize: 3,
    currentPage: recentPage,
  });
  const recentTotalPages = recentMeta?.endPage ?? 1;
  const recentInFlight = recentLoading || recentValidating;
  // 검색 결과 0건 vs 데이터 자체 없음 — 빈 상태 문구 분기용.
  const recentEmptyDueToSearch =
    !recentInFlight &&
    recentItems.length === 0 &&
    recentSubmittedKeyword.length > 0;
  const recentEmptyNoData =
    !recentInFlight &&
    recentItems.length === 0 &&
    recentSubmittedKeyword.length === 0;

  const handleRecentSearch = useCallback(() => {
    setRecentSubmittedKeyword(recentInputKeyword.trim());
    setRecentPage(1);
  }, [recentInputKeyword]);

  const handleRecentPageChange = useCallback((page: number) => {
    setRecentPage(page);
  }, []);

  // 카드 클릭 → 결재라인 통째 덮어쓰기 — 사용자 토글값 무시하고 카드 값으로 갱신(명세).
  const setReferrersAtom = useSetAtom(approvalCreateReferrersAtom);

  /**
   * 새 결재라인에 빠진 고정 협조결재자는 협조 블록에, 고정 결재자는 맨 끝에 다시 얹는다.
   * `keptFlow` 는 resetBeforeApply 가 남겨둔(= 고정만 남은) 직전 flow.
   */
  const mergeFixedIntoFlow = useCallback(
    (nextFlow: FlowEntry[], keptFlow: FlowEntry[]): FlowEntry[] => {
      const nextUserIds = new Set<string>();
      nextFlow.forEach((entry) => {
        if (entry.type === 'member') {
          if (entry.data.userId) nextUserIds.add(entry.data.userId);
        } else {
          entry.members.forEach((m) => {
            if (m.userId) nextUserIds.add(m.userId);
          });
        }
      });

      const missing = keptFlow
        .flatMap((entry) =>
          entry.type === 'member' ? [entry.data] : entry.members,
        )
        .filter((m) => m.userId && !nextUserIds.has(m.userId));
      const missingApprovers = missing.filter((m) => isFixedApprover(m.userId));
      const missingCoApprovers = missing.filter(
        (m) => !isFixedApprover(m.userId),
      );

      const withCoop = (() => {
        if (missingCoApprovers.length === 0) return nextFlow;
        const hasCoop = nextFlow.some((e) => e.type === 'cooperation');
        if (hasCoop) {
          return nextFlow.map((e) =>
            e.type === 'cooperation'
              ? { ...e, members: [...e.members, ...missingCoApprovers] }
              : e,
          );
        }
        return [
          ...nextFlow,
          {
            type: 'cooperation' as const,
            id: makeFlowId('coop-fixed'),
            members: missingCoApprovers,
          },
        ];
      })();
      return pinFixedApproversToEnd(
        [
          ...withCoop,
          ...missingApprovers.map<FlowEntry>((data) => ({
            type: 'member',
            data,
          })),
        ],
        fixedApprovers,
        false,
      );
    },
    [isFixedApprover, fixedApprovers],
  );

  /**
   * 적용 직전 결재라인/협조/참조인 일괄 초기화 — 일반 '초기화' 버튼(결재라인만 비움)과 별개.
   *
   * 양식 고정 항목은 남긴다. 화면에서 사라지면 서버 저장값과 어긋나는데(이 PR 이 고친
   * 원래 증상) 편집 모드에서는 보정(ensure)이 꺼져 있어 스스로 복구되지 않는다.
   * 스낵바의 '퇴사자는 제외하고 반영'은 선택한 카드 범위의 안내라 여기 보존분과 무관하다.
   * 편집 복원 등으로 들어온 퇴사자 고정분이 남을 수는 있으나 잠기지 않아 사용자가 지울 수 있다.
   */
  const resetBeforeApply = useCallback(() => {
    setFlow((prev) =>
      prev.flatMap<FlowEntry>((entry) => {
        const isFixedMember = (m: FlowMemberData) =>
          isFixedCoApprover(m.userId) || isFixedApprover(m.userId);
        if (entry.type === 'member') {
          return isFixedMember(entry.data) ? [entry] : [];
        }
        const members = entry.members.filter(isFixedMember);
        return members.length > 0 ? [{ ...entry, members }] : [];
      }),
    );
    setReferrersAtom((prev) => prev.filter((r) => fixedReferrerIds.has(r.id)));
    setOrderedCooperation(false);
    // 적용하면 열려 있던 상세 시트도 함께 닫는다
    setRecentDetail(null);
  }, [setReferrersAtom, isFixedCoApprover, isFixedApprover, fixedReferrerIds]);

  const applyRecentLine = useCallback(
    (item: RecentApprovalLineSummary) => {
      resetBeforeApply();

      // 퇴사자는 결재라인에 넣지 않고 스낵바로만 안내 — 남겨두면 상신 불가 상태가 됨
      const activeCooperators = (item.cooperators ?? []).filter(
        (c) => !c.deactivated,
      );
      const excludedCount =
        item.approvers.filter((a) => a.deactivated).length +
        (item.cooperators ?? []).filter((c) => c.deactivated).length;

      const seed = `applied-${Date.now()}`;
      const buildCoopBlock = (): Extract<
        FlowEntry,
        { type: 'cooperation' }
      > => ({
        type: 'cooperation',
        id: `${seed}-coop`,
        members: activeCooperators.map((c, ci) => ({
          id: `${seed}-coop-${ci}-${c.passportId}`,
          userId: c.passportId,
          kind: 'approver',
          name: c.name,
          positionName: c.positionName,
          departmentName: c.departmentName,
          profileImageUrl: c.profileImageUrl ?? undefined,
        })),
      });
      const hasCoop = activeCooperators.length > 0;
      // 협조 삽입 위치는 원본 배열 기준 인덱스 — 퇴사자 제외로 밀리지 않게 원본을 순회하며 삽입
      const insertAt = item.cooperationInsertIndex ?? item.approvers.length;
      const newFlow: FlowEntry[] = [];
      item.approvers.forEach((a, i) => {
        if (i === insertAt && hasCoop) newFlow.push(buildCoopBlock());
        if (a.deactivated) return;
        newFlow.push({
          type: 'member',
          data: {
            id: `${seed}-apv-${i}-${a.passportId}`,
            userId: a.passportId,
            kind: 'approver',
            name: a.name,
            positionName: a.positionName,
            departmentName: a.departmentName,
            profileImageUrl: a.profileImageUrl ?? undefined,
          },
        });
      });
      if (hasCoop && insertAt >= item.approvers.length) {
        newFlow.push(buildCoopBlock());
      }

      // 제외 안내가 더 구체적이라 적용 완료 문구보다 우선
      if (excludedCount > 0) {
        snackbar.show({
          message: t('선택한 결재라인의 퇴사자는 제외하고 반영되었습니다.'),
          severity: 'info',
        });
      } else {
        snackbar.showSuccess({
          message: t('선택한 결재라인이 적용되었습니다.'),
        });
      }

      // 카드 값으로 통째로 덮어쓰되, 양식 고정 항목은 다시 얹는다.
      // 편집 모드는 보정(ensure)이 꺼져 있어 여기서 살리지 않으면 화면에서만 사라진다.
      setFlow((prev) => mergeFixedIntoFlow(newFlow, prev));
      setOrderedCooperation(item.cooperationOrdered ?? false);
      setReferrersAtom((prev) => {
        const applied: Referrer[] = (item.referrers ?? []).map((r) => ({
          id: r.passportId,
          name: r.name,
          positionName: r.positionName,
          departmentName: r.departmentName,
          profileImageUrl: r.profileImageUrl ?? undefined,
        }));
        const appliedIds = new Set(applied.map((r) => r.id));
        const keptFixed = prev.filter(
          (r) => fixedReferrerIds.has(r.id) && !appliedIds.has(r.id),
        );
        return [...applied, ...keptFixed];
      });
      setOpenHistory(false);
    },
    [
      resetBeforeApply,
      setReferrersAtom,
      snackbar,
      t,
      mergeFixedIntoFlow,
      fixedReferrerIds,
    ],
  );

  /**
   * 상세 시트는 열린 시점의 item 스냅샷을 들고 있어 그 뒤 도착한 퇴사자 응답이 반영되지 않음.
   * 같은 SWR 키 재구독(카드와 캐시 공유)으로 딱지·적용 대상 모두 최신 값 유지.
   */
  const {
    deactivatedIds: detailDeactivatedIds,
    isLoading: detailDeactivationLoading,
  } = useDeactivatedApprovers(recentDetail?.id);
  const detailItem = useMemo(
    () =>
      recentDetail
        ? withDeactivatedFlags(recentDetail, detailDeactivatedIds)
        : null,
    [recentDetail, detailDeactivatedIds],
  );

  const { passportId: myPassportId } = useAtomValue(MyInfoState);

  // flow에 이미 포함된 사용자 + 본인 id 집합 — 검색 결과/최근 선택에서 제외
  const excludedUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (myPassportId) ids.add(myPassportId);
    flow.forEach((entry) => {
      if (entry.type === 'member') {
        if (entry.data.userId) ids.add(entry.data.userId);
      } else {
        entry.members.forEach((m) => {
          if (m.userId) ids.add(m.userId);
        });
      }
    });
    return ids;
  }, [flow, myPassportId]);

  // 하단 검색바 — 섹션 자신이 Provider 라 context 대신 excludedUserIds 직접 전달
  const {
    list: bottomList,
    showRecent: bottomShowRecent,
    isLoading: bottomLoading,
  } = useSearchPanelData(bottomKeyword, excludedUserIds);

  const bottomRowsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const handleBottomInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    arrowDownIntoList(
      e,
      bottomSearchOpen,
      () => setBottomSearchOpen(true),
      bottomRowsRef.current,
    );

  // 결과행에 포커스가 가 있을 때도 Escape 로 닫고 인풋으로 복귀
  const handleBottomAreaKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !bottomSearchOpen) return;
    if (e.nativeEvent.isComposing) return;
    // focus() 가 SearchInput onFocus(재오픈)를 동기 발화하므로 닫기는 포커스 복귀 뒤에
    bottomSearchInputRef.current?.focus();
    closeBottomSearch();
  };

  const closeHistory = useCallback(() => setOpenHistory(false), []);

  /** PC 팝오버와 모바일 바텀시트가 같은 내용을 담는다 — 한 곳에서만 만든다 */
  const historyBody = (
    <>
      <RecentSearchSection>
        <RecentSearchBar>
          <RecentSearchInput
            inputRef={recentSearchInputRef}
            placeholder={t('제목으로 검색')}
            value={recentInputKeyword}
            onChange={(e) => setRecentInputKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRecentSearch();
              }
            }}
          />
          {recentInputKeyword.length > 0 && (
            <FlowSearchClearButton
              type="button"
              aria-label={t('검색어 지우기')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                // 확정 keyword까지 리셋해야 최근 목록으로 즉시 복귀
                setRecentInputKeyword('');
                setRecentSubmittedKeyword('');
                setRecentPage(1);
                recentSearchInputRef.current?.focus();
              }}
            >
              <CloseCircleIcon width={18} height={18} />
            </FlowSearchClearButton>
          )}
          <RecentSearchButton
            type="button"
            aria-label={t('검색')}
            onClick={handleRecentSearch}
          >
            <SearchIcon width={20} height={20} />
          </RecentSearchButton>
        </RecentSearchBar>
      </RecentSearchSection>
      {recentInFlight && recentItems.length === 0 ? (
        <EmptyHistoryWithIcon>
          <Spinner theme="black" size="20px" />
        </EmptyHistoryWithIcon>
      ) : recentItems.length > 0 ? (
        <RecentApprovalLineList
          items={recentItems}
          currentPage={recentPage}
          totalPages={recentTotalPages}
          onPageChange={handleRecentPageChange}
          showKebab={isMobile}
          loading={recentInFlight}
          activeItemId={recentDetail?.id ?? null}
          onCardKebabClick={
            isMobile ? (item) => setRecentDetail(item) : undefined
          }
          onCardSelect={applyRecentLine}
        />
      ) : recentEmptyDueToSearch ? (
        <EmptyHistoryWithIcon>
          <EmptyHistoryIconWrap>
            <SearchEmptyIcon width={40} height={40} />
          </EmptyHistoryIconWrap>
          <EmptyHistoryText variant="body2">
            {t('‘{{keyword}}’에 해당하는 결재라인이 없습니다.', {
              keyword: recentSubmittedKeyword,
              interpolation: { escapeValue: false },
            })}
          </EmptyHistoryText>
        </EmptyHistoryWithIcon>
      ) : recentEmptyNoData ? (
        <EmptyHistoryWithIcon>
          <EmptyHistoryIconWrap>
            <EmptyIcon width={40} height={40} />
          </EmptyHistoryIconWrap>
          <EmptyHistoryText variant="body2">
            {t('최근 결재라인이 없습니다.')}
          </EmptyHistoryText>
        </EmptyHistoryWithIcon>
      ) : null}
    </>
  );

  return (
    <ExcludedUsersContext.Provider value={excludedUserIds}>
      <MemberStatusContext.Provider value={memberStatus}>
        <SectionBox>
          <SectionTitle clickable={false}>
            <SectionTitleLabel>
              <ApprovalIcon width={24} height={24} />
              {t('결재라인')}
            </SectionTitleLabel>
            <SectionTitleRight>
              <HistoryEntryButton
                ref={setHistoryAnchorEl}
                type="button"
                aria-expanded={openHistory}
                aria-haspopup="dialog"
                onClick={() => setOpenHistory((v) => !v)}
              >
                {t('최근 결재라인')}
              </HistoryEntryButton>
            </SectionTitleRight>
          </SectionTitle>

          <SectionDivider />

          {flow.length > 0 && (
            <>
              {hasRemovableFlowMember && (
                <ApprovalLineActionRow>
                  <ResetTextButton
                    onClick={handleResetFlow}
                    aria-label={t('결재라인 초기화')}
                  />
                </ApprovalLineActionRow>
              )}

              <FlowList>
                <DndContext
                  sensors={sensors}
                  collisionDetection={collisionDetection}
                  modifiers={[restrictToVerticalAxis]}
                  // 소속 이동으로 레이아웃이 바뀌므로 매 프레임 재측정 — 안 하면
                  // 협조 블록 경계 판정이 이동 전 rect 에 굳는다
                  measuring={{
                    droppable: { strategy: MeasuringStrategy.Always },
                  }}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <SortableContext
                    items={entryIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {rendered}
                  </SortableContext>
                  {/* 협조 안팎으로 옮기면 목록이 밀려서, 목록 안 카드를 그대로 끌면
                      커서와 어긋난다. 박스를 끌 때는 안 밀리므로 멤버만 해당.
                      body 로 빼야 한다 — 모달이 넘침을 잘라내서 안에 두면 안 보인다. */}
                  {draggedMember &&
                    createPortal(
                      <DragOverlay
                        dropAnimation={null}
                        modifiers={[restrictToVerticalAxis]}
                        zIndex={DRAG_OVERLAY_Z_INDEX}
                      >
                        <DragOverlayFrame>
                          {dragFromCoop !==
                            (draggedVariant === 'cooperation') && (
                            <DragTypeChangeBadge>
                              {dragFromCoop
                                ? t('일반 결재로 변경')
                                : t('협조결재로 변경')}
                            </DragTypeChangeBadge>
                          )}
                          <FlowItemView
                            data={draggedMember}
                            step={draggedStep}
                            variant={draggedVariant}
                            hideNumberHead={
                              draggedVariant === 'cooperation' &&
                              !orderedCooperation
                            }
                            hideTopRail
                            hideBottomRail
                            hideBottomSpace
                            isDragging
                          />
                        </DragOverlayFrame>
                      </DragOverlay>,
                      document.body,
                    )}
                </DndContext>
              </FlowList>
            </>
          )}

          <SearchSectionContent>
            <ClickAwayListener onClickAway={closeBottomSearch}>
              <BottomSearchPositioner
                onBlur={handleBottomSearchBlur}
                onKeyDown={handleBottomAreaKeyDown}
              >
                <SearchBar>
                  <BoxedTextButton
                    aria-label={t('결재 유형 전환 (현재 {{type}})', {
                      type: t(type, { context: 'role' }),
                    })}
                    endIcon={<SwapIcon width={20} height={20} />}
                    onClick={() =>
                      setType((prev) => (prev === '결재' ? '협조' : '결재'))
                    }
                  >
                    {t(type, { context: 'role' })}
                  </BoxedTextButton>
                  <SearchInput
                    inputRef={bottomSearchInputRef}
                    placeholder={
                      type === '협조'
                        ? t('협조자 이름·소속 검색')
                        : t('결재자 이름·소속 검색')
                    }
                    value={bottomKeyword}
                    onChange={(e) => {
                      setBottomKeyword(e.target.value);
                      // 직전 선택으로 닫혀있던 드롭다운을 타이핑 시 다시 연다.
                      setBottomSearchOpen(true);
                    }}
                    onClick={() => {
                      // 포커스 유지 상태에서 클릭만 한 경우에도 다시 연다.
                      setBottomSearchOpen(true);
                    }}
                    onFocus={() => setBottomSearchOpen(true)}
                    onKeyDown={handleBottomInputKeyDown}
                  />
                  {bottomKeyword ? (
                    <FlowSearchClearButton
                      type="button"
                      aria-label={t('검색어 지우기')}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setBottomKeyword('')}
                    >
                      <CloseCircleIcon width={18} height={18} />
                    </FlowSearchClearButton>
                  ) : (
                    <SearchIconWrapper>
                      <SearchIcon width={20} height={20} />
                    </SearchIconWrapper>
                  )}
                </SearchBar>
                {bottomSearchOpen && (
                  <SearchResultPanel
                    keyword={bottomKeyword}
                    list={bottomList}
                    showRecent={bottomShowRecent}
                    isLoading={bottomLoading}
                    rowsRef={bottomRowsRef}
                    onFocusInput={() => bottomSearchInputRef.current?.focus()}
                    onSelect={handleAppendFromBottomSearch}
                  />
                )}
              </BottomSearchPositioner>
            </ClickAwayListener>
          </SearchSectionContent>

          {/* 최근 결재라인 — 상시 노출 대신 팝오버(PC) / 바텀시트(모바일) */}
          {/* 최근 결재라인 — PC 는 팝오버, 모바일은 바텀시트. 안에 담기는 내용은 같다 */}
          {isMobile ? (
            <MobileBottomSheet
              open={openHistory}
              onClose={closeHistory}
              title={t('최근 결재라인')}
              height="auto"
              maxHeight="80vh"
              // 헤더까지 포함해 총 80vh — content 모드면 헤더 57px 만큼 넘친다
              heightMode="sheet"
              swipeable={false}
            >
              {historyBody}
            </MobileBottomSheet>
          ) : (
            <Popper
              open={openHistory}
              anchorEl={historyAnchorEl}
              placement="bottom-end"
              modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
              style={{ zIndex: 1400 }}
              onKeyDown={(e) => {
                // 팝오버 안에서 Escape 로 닫기 — 검색 패널과 같은 규칙
                if (e.key === 'Escape' && !e.nativeEvent.isComposing) {
                  e.stopPropagation();
                  closeHistory();
                }
              }}
            >
              <ClickAwayListener onClickAway={closeHistory}>
                <HistoryPopoverPaper>
                  <HistoryPopoverHeader>
                    {t('최근 결재라인')}
                    <HistoryPopoverClose
                      type="button"
                      aria-label={t('닫기')}
                      onClick={closeHistory}
                    >
                      <CloseIcon width={13} height={13} />
                    </HistoryPopoverClose>
                  </HistoryPopoverHeader>
                  {historyBody}
                </HistoryPopoverPaper>
              </ClickAwayListener>
            </Popper>
          )}

          <MobileBottomSheet
            open={Boolean(kebabTarget)}
            onClose={closeKebab}
            height="auto"
            showHandle={false}
            swipeable={false}
          >
            <KebabSheetContainer role="menu">
              <KebabSheetItem
                type="button"
                role="menuitem"
                onClick={handleKebabInsertNext}
              >
                <KebabSheetItemIcon>
                  <AddNextLineIcon
                    width={KEBAB_SHEET_ICON_BOX}
                    height={KEBAB_SHEET_ICON_BOX}
                  />
                </KebabSheetItemIcon>
                <span>{t('다음 결재라인 추가')}</span>
              </KebabSheetItem>
              {!kebabTargetLocked && (
                <KebabSheetItem
                  type="button"
                  role="menuitem"
                  onClick={handleKebabSwap}
                >
                  <KebabSheetItemIcon>
                    <SwapIcon
                      width={KEBAB_SHEET_ICON_BOX}
                      height={KEBAB_SHEET_ICON_BOX}
                    />
                  </KebabSheetItemIcon>
                  <span>
                    {kebabTarget?.kind === 'coop'
                      ? t('일반 결재로 변경')
                      : t('협조결재로 변경')}
                  </span>
                </KebabSheetItem>
              )}
              {!kebabTargetLocked && (
                <KebabSheetItem
                  type="button"
                  role="menuitem"
                  danger
                  onClick={handleKebabRemove}
                >
                  <KebabSheetItemIcon>
                    <CloseIcon
                      width={KEBAB_SHEET_CLOSE_ICON}
                      height={KEBAB_SHEET_CLOSE_ICON}
                      style={{
                        ['--icon-stroke-width' as string]:
                          KEBAB_SHEET_CLOSE_STROKE,
                      }}
                    />
                  </KebabSheetItemIcon>
                  <span>{t('결재라인에서 삭제')}</span>
                </KebabSheetItem>
              )}
            </KebabSheetContainer>
          </MobileBottomSheet>

          {/* 목록이 팝오버로 들어가면서 PC 호버 상세는 뺐다 (팝오버 위에 팝오버가 겹침).
              카드가 순번·이름·구성 요약을 직접 보여주므로 상세는 모바일 케밥에서만 연다. */}
          <RecentApprovalLineDetail
            item={isMobile ? detailItem : null}
            variant="drawer"
            anchorEl={null}
            lineSource={activeTab}
            onClose={() => setRecentDetail(null)}
            onApply={applyRecentLine}
            applyDisabled={detailDeactivationLoading}
            onAddToMyLines={() => {
              setRecentDetail(null);
              showComingSoon();
            }}
            onRenameLine={() => {
              setRecentDetail(null);
              showComingSoon();
            }}
            onDeleteLine={() => {
              setRecentDetail(null);
              showComingSoon();
            }}
          />
        </SectionBox>
      </MemberStatusContext.Provider>
    </ExcludedUsersContext.Provider>
  );
};

export default ApprovalLineSection;
